import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { logErr } from "../lib/utils-trel";
import { Wand2, Sparkles, ChevronRight, Loader2, FileText, Lock, Send } from "lucide-react";

const NAVY = "#071A2D";
const GOLD = "#C9A45C";
const EMERALD = "#064E3B";
const IVORY = "#FFF8EE";
const OCEAN = "#00AEEF";

/**
 * AIExperienceAssistant
 * TREL+ premium feature for verified concierges.
 * Generates proposal-style "TREL Experience Plans" from a customer's request.
 *
 * Locked state shown to non-TREL+ providers (drives upgrade).
 */
export default function AIExperienceAssistant({ provider, onUpgrade }) {
  const isPlus = !!provider?.trel_plus_active;
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_request: "",
    customer_name: "",
    city: provider?.city || "",
    date: "",
    guests: 4,
    budget: 1500,
    event_type: "Birthday",
    luxury_level: "premium",
    food_preferences: "",
    music_preferences: "",
    special_requests: "",
  });
  const [activePlan, setActivePlan] = useState(null);
  const [feeInput, setFeeInput] = useState("");
  const [saving, setSaving] = useState(false);

  const loadPlans = useCallback(async () => {
    if (!isPlus) return;
    try {
      const { data } = await api.get("/providers/experience-plans");
      setPlans(Array.isArray(data) ? data : []);
    } catch (e) { logErr("experience-plans/load", e); }
  }, [isPlus]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const generate = async () => {
    if (!form.customer_request.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/providers/experience-plan/generate", form);
      setActivePlan(data);
      setFeeInput(String(data?.plan?.concierge_service_fee ?? ""));
      await loadPlans();
    } catch (e) {
      logErr("experience-plan/generate", e);
    }
    setLoading(false);
  };

  const updateFee = async () => {
    if (!activePlan) return;
    const fee = parseFloat(feeInput);
    if (Number.isNaN(fee) || fee < 0) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/providers/experience-plans/${activePlan.plan_id}`, {
        concierge_service_fee: fee,
      });
      setActivePlan(data);
      await loadPlans();
    } catch (e) {
      logErr("experience-plan/fee", e);
    }
    setSaving(false);
  };

  const setStatus = async (next) => {
    if (!activePlan) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/providers/experience-plans/${activePlan.plan_id}`, {
        status: next,
      });
      setActivePlan(data);
      await loadPlans();
    } catch (e) {
      logErr("experience-plan/status", e);
    }
    setSaving(false);
  };

  if (!isPlus) {
    return (
      <section data-testid="ai-experience-assistant-locked" className="mt-10 rounded-3xl overflow-hidden border border-[#071A2D]/10 shadow-luxury">
        <div className="p-7 sm:p-9 bg-gradient-to-br from-[#FFFDF8] via-[#FFF8EE] to-[#FAEFD8]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${GOLD}22`, color: "#8a6a2e" }}>
              <Lock size={20}/>
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>TREL+ Premium Business Tools</div>
              <h3 className="font-serif text-2xl sm:text-3xl mt-2" style={{ color: NAVY }}>
                TREL+ AI Business Assistant
              </h3>
              <p className="mt-3 text-sm sm:text-base" style={{ color: "rgba(7,26,45,0.72)" }}>
                AI-powered tools for proposals, pricing, and growth — built only
                for TREL+ Concierges. Turn any customer request into a polished,
                proposal-style <b>TREL Experience Plan</b> with vision summary,
                itinerary, vendors, cost breakdown, and your service fee.
              </p>
              <div className="mt-3 text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: EMERALD }}>
                TREL+ ≠ trust badges. TREL+ unlocks business tools. Elite is the highest trust level.
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm" style={{ color: "rgba(7,26,45,0.85)" }}>
                {[
                  "Customer proposals & itineraries",
                  "Pricing suggestions",
                  "Package & add-on ideas",
                  "Follow-up message drafts",
                  "Marketing & social captions",
                  "Referral/QR promo copy",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Sparkles size={14} style={{ color: GOLD }} className="mt-1 shrink-0"/>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <button onClick={onUpgrade} className="btn-coral inline-flex items-center gap-2" data-testid="ai-assistant-upgrade-cta">
                  Upgrade to TREL+ — $29/mo <ChevronRight size={16}/>
                </button>
                <div className="text-[11px] mt-3" style={{ color: "rgba(7,26,45,0.55)" }}>
                  TREL+ is optional. Joining as a Concierge is always free.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="ai-experience-assistant" className="mt-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>TREL+ AI Business Assistant</div>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1" style={{ color: NAVY }}>Turn a request into a proposal.</h2>
          <p className="mt-2 text-sm" style={{ color: "rgba(7,26,45,0.65)" }}>
            Paste your customer&apos;s vision. AI generates a complete TREL Experience Plan you can review, edit, and send.
          </p>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        {/* INPUT FORM */}
        <div className="lg:col-span-5 rounded-3xl bg-white border border-[#071A2D]/10 shadow-luxury p-6">
          <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: EMERALD }}>Customer request</label>
          <textarea
            data-testid="ai-assistant-request"
            rows={4}
            value={form.customer_request}
            onChange={(e) => setForm({ ...form, customer_request: e.target.value })}
            placeholder='"Plan my birthday weekend in Washington D.C. for 8 friends — VIP nightlife and a surprise dinner."'
            className="mt-2 w-full rounded-xl border border-[#071A2D]/10 bg-[#FFF8EE] p-3 text-sm outline-none focus:border-[#00AEEF] focus:ring-2 focus:ring-[#00AEEF]/20"
          />

          <div className="grid grid-cols-2 gap-3 mt-4">
            <Field label="Customer name">
              <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="City">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Date">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Guests">
              <input type="number" value={form.guests} onChange={(e) => setForm({ ...form, guests: +e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Budget ($)">
              <input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: +e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Event type">
              <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="kbd-input">
                {["Birthday","Anniversary","Proposal","VIP Nightlife","Corporate Retreat","Executive Celebration","Client Entertainment","Date Night","Bachelor/Bachelorette","Group Celebration","Other"].map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Luxury level">
              <select value={form.luxury_level} onChange={(e) => setForm({ ...form, luxury_level: e.target.value })} className="kbd-input">
                <option value="premium">Premium</option>
                <option value="luxury">Luxury</option>
                <option value="ultra">Ultra-luxury</option>
              </select>
            </Field>
            <Field label="Food preferences">
              <input value={form.food_preferences} onChange={(e) => setForm({ ...form, food_preferences: e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Music preferences">
              <input value={form.music_preferences} onChange={(e) => setForm({ ...form, music_preferences: e.target.value })} className="kbd-input"/>
            </Field>
            <Field label="Special requests" col2>
              <input value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} className="kbd-input"/>
            </Field>
          </div>

          <button
            onClick={generate}
            disabled={loading || !form.customer_request.trim()}
            data-testid="ai-assistant-generate"
            className="mt-5 w-full btn-coral inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 size={16} className="animate-spin"/> Designing plan…</> : <><Wand2 size={16}/> Generate Experience Plan</>}
          </button>
        </div>

        {/* OUTPUT — Plan preview */}
        <div className="lg:col-span-7">
          {!activePlan && (
            <div className="rounded-3xl bg-white border border-dashed border-[#071A2D]/15 p-10 text-center" data-testid="ai-assistant-empty">
              <FileText size={28} className="mx-auto" style={{ color: "rgba(7,26,45,0.35)" }}/>
              <div className="font-serif text-xl mt-3" style={{ color: NAVY }}>No plan generated yet</div>
              <p className="text-sm mt-2" style={{ color: "rgba(7,26,45,0.55)" }}>
                Fill in the customer request on the left and click Generate. Your saved plans will appear below.
              </p>
            </div>
          )}
          {activePlan && <PlanPreview plan={activePlan} feeInput={feeInput} setFeeInput={setFeeInput} onUpdateFee={updateFee} onStatus={setStatus} saving={saving}/>}
        </div>
      </div>

      {/* SAVED PLANS LIST */}
      {plans.length > 0 && (
        <div className="mt-10" data-testid="ai-assistant-saved">
          <h3 className="font-serif text-xl" style={{ color: NAVY }}>Recent plans</h3>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map(p => (
              <button
                key={p.plan_id}
                onClick={() => { setActivePlan(p); setFeeInput(String(p?.plan?.concierge_service_fee ?? "")); }}
                className="text-left rounded-2xl border border-[#071A2D]/10 bg-white hover:border-[#C9A45C] hover:shadow-luxury transition-all p-4"
                data-testid={`ai-assistant-plan-${p.plan_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: GOLD }}>{p.status}</div>
                  <div className="text-xs" style={{ color: "rgba(7,26,45,0.55)" }}>{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <div className="font-serif text-lg mt-2" style={{ color: NAVY }}>
                  {p.plan?.event_overview?.slice(0, 70) || p.customer_request.slice(0, 70)}…
                </div>
                <div className="mt-2 text-xs" style={{ color: "rgba(7,26,45,0.55)" }}>
                  ${p.plan?.final_customer_estimate?.toLocaleString?.() || "—"} estimate · {p.plan?.guest_count || p.input?.guests || "?"} guests
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ----- Sub-components ----- */
const Field = ({ label, children, col2 }) => (
  <label className={`block ${col2 ? "col-span-2" : ""}`}>
    <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: "rgba(7,26,45,0.55)" }}>{label}</div>
    {children}
  </label>
);

function PlanPreview({ plan, feeInput, setFeeInput, onUpdateFee, onStatus, saving }) {
  const p = plan?.plan || {};
  const costs = Array.isArray(p.estimated_costs) ? p.estimated_costs : [];
  const baseTotal = costs.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  return (
    <div className="rounded-3xl bg-white border border-[#071A2D]/10 shadow-luxury-lg overflow-hidden" data-testid="ai-assistant-plan-preview">
      {/* Cover */}
      <div className="p-6 sm:p-8" style={{ background: `linear-gradient(135deg, #FFFDF8 0%, ${IVORY} 60%, #FAEFD8 100%)` }}>
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>TREL Experience Plan</div>
          <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold" style={{ background: `${EMERALD}15`, color: EMERALD }}>{plan.status}</span>
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl mt-3" style={{ color: NAVY }}>{p.event_overview || "Your bespoke experience"}</h3>
        <p className="mt-3 text-sm" style={{ color: "rgba(7,26,45,0.72)" }}>
          <b>Vision:</b> {p.vision_summary}
        </p>
      </div>

      {/* Body */}
      <div className="p-6 sm:p-8 space-y-7">
        {/* Itinerary */}
        {Array.isArray(p.recommended_itinerary) && p.recommended_itinerary.length > 0 && (
          <Block title="Recommended itinerary">
            <ol className="space-y-3">
              {p.recommended_itinerary.map((s, i) => (
                <li key={`${s.time}-${s.title}-${i}`} className="flex gap-3">
                  <div className="font-mono text-xs px-2 py-1 rounded-md bg-[#FFF8EE] border border-[#071A2D]/10 shrink-0" style={{ color: EMERALD }}>{s.time}</div>
                  <div>
                    <div className="font-medium" style={{ color: NAVY }}>{s.title}</div>
                    <div className="text-sm" style={{ color: "rgba(7,26,45,0.7)" }}>{s.description}</div>
                    {s.location && <div className="text-xs mt-0.5" style={{ color: "rgba(7,26,45,0.5)" }}>📍 {s.location}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </Block>
        )}

        {/* Vendors */}
        {Array.isArray(p.vendors_and_services) && p.vendors_and_services.length > 0 && (
          <Block title="Vendors & services included">
            <div className="flex flex-wrap gap-2">
              {p.vendors_and_services.map((v, i) => (
                <span key={`${v}-${i}`} className="text-xs px-3 py-1.5 rounded-full border" style={{ background: IVORY, borderColor: "rgba(7,26,45,0.10)", color: NAVY }}>{v}</span>
              ))}
            </div>
          </Block>
        )}

        {/* Logistics */}
        <Block title="Logistics">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Guests" value={p.guest_count || "—"}/>
            <Stat label="Locations" value={(p.locations || []).join(" · ") || "—"}/>
            <Stat label="Status" value={plan.status}/>
          </div>
          {p.special_requests && p.special_requests !== "None noted." && (
            <div className="mt-3 text-sm" style={{ color: "rgba(7,26,45,0.7)" }}><b>Special requests:</b> {p.special_requests}</div>
          )}
        </Block>

        {/* Cost breakdown */}
        <Block title="Estimated costs">
          <div className="rounded-2xl border border-[#071A2D]/10 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {costs.map((c, i) => (
                  <tr key={`${c.item}-${i}`} className="border-b border-[#071A2D]/5 last:border-0">
                    <td className="px-4 py-3" style={{ color: NAVY }}>{c.item}</td>
                    <td className="px-4 py-3 text-right font-mono" style={{ color: NAVY }}>${Number(c.amount).toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-[#FFF8EE]">
                  <td className="px-4 py-3 text-sm font-semibold" style={{ color: NAVY }}>Subtotal</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold" style={{ color: NAVY }}>${baseTotal.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Editable concierge service fee */}
          <div className="mt-4 rounded-2xl p-4 flex items-center justify-between gap-4" style={{ background: `${GOLD}10`, border: `1px solid ${GOLD}55` }}>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#8a6a2e" }}>Your concierge service fee</div>
              <div className="text-xs mt-0.5" style={{ color: "rgba(7,26,45,0.65)" }}>Adjust to reflect your value. ~15% recommended.</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono" style={{ color: NAVY }}>$</span>
              <input
                data-testid="ai-assistant-fee-input"
                type="number"
                min="0"
                value={feeInput}
                onChange={(e) => setFeeInput(e.target.value)}
                className="w-24 px-2 py-1.5 rounded-md border border-[#C9A45C] bg-white font-mono text-right outline-none focus:ring-2 focus:ring-[#C9A45C]/30"
              />
              <button onClick={onUpdateFee} disabled={saving} data-testid="ai-assistant-fee-save" className="btn-outline-emerald text-xs px-3 py-1.5">
                {saving ? "…" : "Update"}
              </button>
            </div>
          </div>

          {/* Final estimate */}
          <div className="mt-4 rounded-2xl p-5 flex items-center justify-between" style={{ background: EMERALD, color: IVORY }}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Final customer estimate</div>
              <div className="text-xs opacity-80 mt-1">All-in price you can present to your customer</div>
            </div>
            <div className="font-serif text-3xl">${Number(p.final_customer_estimate || 0).toLocaleString()}</div>
          </div>
        </Block>

        {/* Note */}
        {p.personal_note_from_concierge && (
          <Block title="A note from your concierge">
            <div className="rounded-2xl border p-4 italic text-sm" style={{ background: `${OCEAN}08`, borderColor: `${OCEAN}30`, color: "rgba(7,26,45,0.8)" }}>
              &ldquo;{p.personal_note_from_concierge}&rdquo;
            </div>
          </Block>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <button onClick={() => onStatus("sent")} disabled={saving || plan.status === "sent"} data-testid="ai-assistant-mark-sent" className="btn-coral inline-flex items-center gap-2 text-sm">
            <Send size={14}/> Mark as sent
          </button>
          <button onClick={() => onStatus("accepted")} disabled={saving} className="btn-outline-emerald text-sm">Mark accepted</button>
          <button onClick={() => onStatus("declined")} disabled={saving} className="text-sm px-4 py-2 rounded-full text-[#071A2D]/65 hover:text-[#071A2D]">Decline</button>
        </div>
      </div>
    </div>
  );
}

const Block = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-2.5" style={{ color: EMERALD }}>{title}</div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-[#FFF8EE] border border-[#071A2D]/10 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(7,26,45,0.55)" }}>{label}</div>
    <div className="text-sm font-medium mt-0.5" style={{ color: NAVY }}>{value}</div>
  </div>
);
